// InquirySection1.tsx
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import theme from '../../common/color/theme';
import {
  CenterLayout,
  HorizontalLayout,
  useResponsivePadding,
  VerticalLayout
} from '../../common/common-layout';
import {
  Body1,
  Body3,
  H3,
  Text4,
} from '../../common/common-text';
import BTBox from '../../common/components/BTBox';
import BTButton from '../../common/components/BTButton';
import { BTInput } from '../../common/components/BTInput';
import { BTTextArea } from '../../common/components/BTTextArea';
import { pxToRem } from '../../common/utils/ConvertUtils';
import '../../css/bbpage.css';
import { useDeviceType } from '../../hooks/useDeviceType';
import { customerInquirySchema } from '../../schema/customer/CustomerInquirySchema';
import { sendCustomerInquiry } from '../../service/CustomerService';
import { baseStore } from '../../store/BaseStore';

const InquirySection1 = () => {
  const { t } = useTranslation();
  const { horizontal: padding } = useResponsivePadding();
  const [requestorName, setRequestorName] = useState('');
  const [email, setEmail] = useState('');
  const [title, setTitle] = useState('');
  const [contents, setContents] = useState('');
  const { isMobile } = useDeviceType();
  const [isSending, setIsSending] = useState(false);

  const maxLength = 1000;

  // 문의 요청 함수
  const handleSendInquiry = async () => {
    /// 모든 입력값이 유효할 떄만 진행
    const validation = customerInquirySchema.safeParse({
      requestorName,
      email,
      title,
      contents,
    });

    if (!validation.success) {
      alert(validation.error.errors[0].message);
      return;
    }

    try {
      baseStore.showLoading();
      const inquiry = {
        requestorName,
        email,
        title,
        contents: contents,
        replyCompletedYn: false,
        createdAt: new Date(),
      };

      await sendCustomerInquiry(inquiry);
      alert('제출이 완료되었습니다.');

      // 입력 필드 초기화
      setRequestorName('');
      setEmail('');
      setTitle('');
      setContents('');
      baseStore.hideLoading();
    } catch (error) {
      alert('제출에 실패했습니다. 다시 시도해주세요.');
      console.error('문의 제출 오류:', error);
      baseStore.hideLoading();
    }
  };

  return (
    <VerticalLayout paddingLeft={padding} paddingRight={padding} style={{ background: 'white' }}>
      <BTBox height={isMobile ? 40 : `${pxToRem(100)}`} />
      <H3>Contact Us</H3>
      <BTBox height={isMobile ? 8 : `${pxToRem(13)}`} />
      <Body1 style={{ opacity: '0.5' }}>{t('궁금한 점이나 문의사항이 있으시다면 언제든지 편하게 연락 주세요.')}</Body1>
      <BTBox height={isMobile ? 23 : `${pxToRem(60)}`} />
      <VerticalLayout height={1} backgroundColor='black' style={{ opacity: 0.6 }} />
      <BTBox height={isMobile ? 23 : `${pxToRem(64)}`} />

      <HorizontalLayout
        style={{
          alignItems: isMobile ? 'start' : 'center',
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        <Body3
          style={{
            width: isMobile ? '100%' : '300px',
            marginBottom: isMobile ? '8px' : undefined,
          }}
        >
          {t('작성자')}<span style={{ color: theme.colors.BIBI }}>*</span>
        </Body3>
        <BTInput
          style={{
            width: isMobile ? '100%' : '511px',
          }}
          value={requestorName}
          onChange={(e) => setRequestorName(e.target.value)}
        />
      </HorizontalLayout>

      <BTBox height={isMobile ? 16 : pxToRem(40)} />

      <HorizontalLayout
        style={{
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'flex-start' : 'center',
          gap: isMobile ? '8px' : '0',
        }}
      >
        <Body3 style={{ width: isMobile ? '100%' : '300px' }}>
          {t('이메일')}<span style={{ color: theme.colors.BIBI }}>*</span>
        </Body3>

        <BTInput
          style={{
            width: isMobile ? '100%' : '511px',
          }}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </HorizontalLayout>

      <BTBox height={isMobile ? 16 : pxToRem(40)} />

      <HorizontalLayout
        style={{
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'flex-start' : 'center',
          gap: isMobile ? '8px' : '0',
        }}
      >
        <Body3 style={{ width: isMobile ? '100%' : '300px' }}>
          {t('제목')}<span style={{ color: theme.colors.BIBI }}>*</span>
        </Body3>

        <BTInput
          style={{
            flex: isMobile ? undefined : 1,
            width: isMobile ? '100%' : undefined,
          }}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </HorizontalLayout>

      <BTBox height={isMobile ? 16 : pxToRem(40)} />

      <HorizontalLayout
        style={{
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'start',
          gap: isMobile ? '8px' : '0', // 모바일일 때 간격 추가
        }}
      >
        <Body3 style={{ width: isMobile ? '100%' : '300px' }}>
          {t('문의 내용')}<span style={{ color: theme.colors.BIBI }}>*</span>
        </Body3>
        <BTTextArea
          placeholder={t('내용을 작성해 주세요.')}
          style={{ flex: 1, minHeight: isMobile ? '218px' : '473px' }}
          value={contents}
          onChange={(e) => setContents(e.target.value)}
        />
      </HorizontalLayout>

      <BTBox height={isMobile ? 4 : pxToRem(20)} />

      <HorizontalLayout>
        <Text4 style={{
          paddingLeft: isMobile ? '0px' : '300px', color: '#979797',
          ...isMobile ? { fontSize: '11px' } : {}
        }}>
          {t('10자 이상 1000자 이하로 입력해주세요.')}
        </Text4>
        <div style={{ flex: 1 }} />
        <Text4 style={{
          color: '#979797',
          ...isMobile ? { fontSize: '11px' } : {}
        }}>
          ({contents.length}/{maxLength})
        </Text4>
      </HorizontalLayout>
      <BTBox height={isMobile ? 40 : `${pxToRem(40)}`} />
      <VerticalLayout height={1} backgroundColor='#00000033' />
      <BTBox height={isMobile ? 40 : `${pxToRem(142)}`} />
      <CenterLayout>
        <BTButton width={isMobile ? '100%' : `${pxToRem(359)}`} height={isMobile ? '60px' : `${pxToRem(102)}`} color={theme.colors.BIBI} textColor='white'
          sx={{
            fontWeight: 700,
            fontSize: isMobile ? '15px' : `${pxToRem(28)}`,
            '&:hover': {
              boxShadow: 'none',
              textDecoration: 'underline',
              textUnderlineOffset: '2px',
              textDecorationThickness: '2px',
              borderColor: 'transparent', 
            },
          }}
          onClick={handleSendInquiry} data-analytics-name="문의하기">{t('문의하기')}</BTButton>
      </CenterLayout>
      <BTBox height={isMobile ? 130 : 300} />
    </VerticalLayout >
  );
};

export default InquirySection1;
